import React from "react";
import { Link } from "react-router-dom";

import { LegalEntityNameChangeStatus } from "../../../../components/LegalEntityNameChangeStatus";
import { xboxOnboardSiteLocationPrefix } from "../../../../constants";
import { useString, useStrings } from "../../../../localization/localizationHelpers";
import type { LegalEntityNameChangeState } from "../../../../server/crm/api/models/xboxOnboardModels";
import { PartnerCardMenu } from "../PartnerCardMenu";
import * as styles from "../../PartnerHomePage.module.css";

const AgreementsLinkPlaceholder = "__AGREEMENTS_LINK__";

type PartnerNdaCardProps = Readonly<{
  contactEmail: string | undefined;
  ndaSentDate: string | undefined;
  ndaNumber?: string;
  hasNdaId?: boolean;
  legalEntityNameChange?: LegalEntityNameChangeState;
  hasAttachedBanner?: boolean;
  managePath: string;
  showAgreementsOption?: boolean;
  showDevReadinessGuideOption: boolean;
  showSettingsOption?: boolean;
  settingsOnlyMenu?: boolean;
  onShowDevReadinessGuide: () => void;
}>;

export const PartnerNdaCard = ({
  contactEmail,
  ndaSentDate,
  ndaNumber,
  hasNdaId,
  legalEntityNameChange,
  hasAttachedBanner = false,
  managePath,
  showAgreementsOption,
  showDevReadinessGuideOption,
  showSettingsOption,
  settingsOnlyMenu,
  onShowDevReadinessGuide,
}: PartnerNdaCardProps) => {
  const [
    partnerDetailsLabel,
    partnerDetailsMessage,
    legalNameNdaMessage,
    signNdaLabel,
    agreementsLabel,
    ndaPreparingTitle,
    ndaPreparingDuration,
  ] = useStrings(
    "partnerHomePartnerDetailsLabel",
    "partnerHomePartnerDetailsMessage",
    "partnerHomePartnerDetailsLegalNameNdaMessage",
    "devGuideSignNda",
    "agreementsLabel",
    "partnerHomeNdaPreparingTitle",
    "partnerHomeNdaPreparingDuration"
  );
  const ndaPreparingMessage = useString("partnerHomeNdaPreparingMessage", contactEmail ?? "");
  const formattedDate = ndaSentDate
    ? new Date(ndaSentDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";
  const ndaSentMessage = useString(
    ndaSentDate ? "partnerLegalNdaSentWithDateMessage" : "partnerLegalNdaSentMessage",
    contactEmail ?? "",
    formattedDate
  );
  const processingNotice = useString("partnerLegalNdaProcessingNotice", AgreementsLinkPlaceholder);
  const [processingNoticeStart, processingNoticeEnd = ""] =
    processingNotice.split(AgreementsLinkPlaceholder);

  return (
    <div
      className={`${styles.cardDashedFull} ${hasAttachedBanner ? styles.cardAttachedBottom : ""}`}
    >
      <div className={styles.cardHeaderRow}>
        <div>
          <p className={styles.cardLabel}>{partnerDetailsLabel}</p>
          <p className={styles.cardMessage}>
            {legalEntityNameChange?.hasNdaSent ? legalNameNdaMessage : partnerDetailsMessage}
          </p>
        </div>
        <PartnerCardMenu
          settingsOnly={settingsOnlyMenu}
          managePath={managePath}
          showAgreementsOption={showAgreementsOption}
          showDevReadinessGuideOption={showDevReadinessGuideOption}
          showSettingsOption={showSettingsOption ?? false}
          onShowDevReadinessGuide={onShowDevReadinessGuide}
        />
      </div>
      {legalEntityNameChange?.hasNdaSent ? (
        <LegalEntityNameChangeStatus
          state={legalEntityNameChange}
          contactEmail={contactEmail}
          attached
          flush
        />
      ) : hasNdaId === false && !ndaNumber ? (
        <>
          <div className={styles.cardDivider} />
          <div className={styles.actionItemLeft}>
            <span className={styles.statusDot} aria-hidden="true" />
            <span className={styles.actionItemLabel}>{ndaPreparingTitle}</span>
          </div>
          <p className={styles.ndaActionDescription}>
            {ndaPreparingMessage}
            <br />
            <br />
            <strong>{ndaPreparingDuration}</strong>
          </p>
        </>
      ) : (
        <>
          <div className={styles.cardDivider} />
          <Link className={styles.cardActionRow} to={`${xboxOnboardSiteLocationPrefix}/agreements`}>
            <div className={styles.actionItemLeft}>
              <span className={styles.statusDot} aria-hidden="true" />
              <span className={styles.actionItemLabel}>
                {ndaNumber ? `${signNdaLabel} #${ndaNumber}` : signNdaLabel}
              </span>
            </div>
            <span className={styles.actionItemChevron} aria-hidden="true">
              &rsaquo;
            </span>
          </Link>
          <p className={styles.ndaActionDescription}>
            {ndaSentMessage}
            <br />
            <br />
            {processingNoticeStart}
            <Link to={`${xboxOnboardSiteLocationPrefix}/agreements`} className={styles.supportLink}>
              {agreementsLabel}
            </Link>
            {processingNoticeEnd}
          </p>
        </>
      )}
    </div>
  );
};
